import type { ViewFieldsFragment as ClientApiViewFieldsFragment } from '@salescore/client-api'
import {
  getMetricFromKpiFormula,
  type KpiFormulaNodeWithMetric,
  kpiFormulaNodeWithMetricSchema,
  type Metric,
  type ViewConfig,
  type ViewConfigKpi,
  viewConfigKpiToMetric,
  type ViewConfigKpiWithMetric,
  type ViewConfigWithMetric,
} from '@salescore/core'

export type ViewFieldsFragmentWithMetric = Omit<ClientApiViewFieldsFragment, 'config'> & {
  config: ViewConfigWithMetric
}

/**
 * GraphQL scalar 経由で受け取った kpi config の `formula` を `KpiFormulaNodeWithMetric` へ回復する。
 * client-api の `ViewConfig` 型は `formula` を metric なしの `KpiFormulaNode` で公開するが、
 * ランタイム JSON には BE producer (`attachMetricToView`) が inline から derive して attach した
 * `formula.metric` が乗っている。型がそれを露出しないため zod parse で型安全に持ち上げる。
 *
 * parse 失敗（ref だが metric 不在 = 未 attach のレスポンスを表示に流した過渡状態 / inline 不足）は
 * `{ type: 'empty' }` を返し、compile 側の「KPI 設定未完了」warn 動線へ流す。
 */
export const parseKpiFormulaWithMetric = (formula: ViewConfigKpi['formula']): KpiFormulaNodeWithMetric => {
  const parsed = kpiFormulaNodeWithMetricSchema.safeParse(formula)
  return parsed.success ? parsed.data : { type: 'empty' }
}

/** 永続 `formula.metric` から Metric を取り出す。 */
export const getMetricFromViewConfigKpi = (config: ViewConfigKpi): Metric | undefined =>
  getMetricFromKpiFormula(parseKpiFormulaWithMetric(config.formula))

/**
 * 表示 compile 用 producer。永続 `formula.metric` を source of truth として、
 * `config.formula` を `KpiFormulaNodeWithMetric` 形に持ち上げる。compile 経路は
 * `ViewConfigKpiWithMetric` 必須なので、本関数を通過した戻り値だけが compile に渡せる。
 */
export const attachMetricToKpiConfigFromFormula = (config: ViewConfigKpi): ViewConfigKpiWithMetric => ({
  ...config,
  formula: parseKpiFormulaWithMetric(config.formula),
})

/**
 * `ViewConfig` union を表示 compile 用に `ViewConfigWithMetric` に持ち上げる FE 用 attach。
 * kpi variant のみ `attachMetricToKpiConfigFromFormula` で formula を型回復し、それ以外
 * (sheet / form / kpiPivot) は構造的に `ViewConfigWithMetric` の部分集合なのでそのまま返す。
 */
export const attachMetricToConfigFromFormula = (config: ViewConfig): ViewConfigWithMetric =>
  config.type === 'kpi' ? attachMetricToKpiConfigFromFormula(config) : config

/**
 * GraphQL scalar 経由の view を表示経路用に 1 回だけ型回復する。kpi variant のみ
 * formula を WithMetric 形へ持ち上げ、sheet / form / kpiPivot は構造的部分集合なので素通し。
 */
export const attachMetricToViewFieldsFromFormula = (
  view: ClientApiViewFieldsFragment,
): ViewFieldsFragmentWithMetric => ({
  ...view,
  config: attachMetricToConfigFromFormula(view.config),
})

/**
 * KPI フォームの live inline (`sheet/measure/date/user`) を source of truth として、
 * `config.formula` を `KpiFormulaNodeWithMetric` 形に再 derive する。編集プレビュー専用。
 *
 * metric 導出可否に応じて 2 通りの formula を必ず設定する:
 *   - inline 揃い → `{ type: 'ref', metricId, metric }`
 *   - inline 不足 → `{ type: 'empty' }` (compile 側で warn 経路 / UI 側で「KPI 設定未完了」表示)
 * inline→metric 変換は `viewConfigKpiToMetric` に一意化。
 */
export const attachMetricToKpiConfigFromInline = (config: ViewConfigKpi, viewId: string): ViewConfigKpiWithMetric => {
  const metric = viewConfigKpiToMetric(config)
  const formula: KpiFormulaNodeWithMetric =
    metric === undefined ? { type: 'empty' } : { type: 'ref', metricId: viewId, metric }
  return { ...config, formula }
}
